// src/pages/LitMatrixSection.jsx
//
// Student-facing render of a THEME MATRIX lit_section (sectionKind 'matrix'). Faithful port
// of the prototype LitMatrixView (between-the-lines-v2.html L13114-13159): a sticky-header
// Theme x Character grid where every cell is a ready-to-quote point; click a cell to expand.
//
// Reads MATRIX_BLOCKS (litSectionShape.js, mirrors LitThemeMatrixEditor): a SINGLETON
// section whose content.blocks = {
//   matrixChars:  [string],                     // ordered COLUMN axis
//   matrixThemes: [string],                     // ordered ROW axis
//   matrix: { [character]: { [theme]: text } }  // sparse cell map -> matrix[col][row]
// }. Relies on the .matrix-* / .custom-scrollbar CSS in src/index.css (incl. day-mode
// overrides), so it inherits day/night theming.
//
// Pure presentational (takes `content`). DEVIATION from the prototype: the active cell is
// tracked as a {ch,th} object rather than a `theme/ch` joined string -- avoids the prototype's
// split('/') bug when an axis label itself contains a slash. Behaviour is otherwise identical.
import React, { useState } from 'react';
import LessonHeader from '../components/reader/LessonHeader.jsx';

// PA4: the axis WORDS are configurable so the same component serves literature
// (Theme x Character) and poetry (Poem x Theme). Data keys stay matrixChars/matrixThemes;
// only the DISPLAY strings change. Defaults reproduce the literature reader byte-for-byte.
export default function LitMatrixSection({ content, done = false, titleFallback = 'Theme × Character Matrix', groupTag = 'Extras · Matrix' }) {
    const blocks = (content && content.blocks) || {};
    const chars = Array.isArray(blocks.matrixChars) ? blocks.matrixChars : [];
    const themes = Array.isArray(blocks.matrixThemes) ? blocks.matrixThemes : [];
    const matrix = (blocks.matrix && typeof blocks.matrix === 'object' && !Array.isArray(blocks.matrix)) ? blocks.matrix : {};
    const [active, setActive] = useState(null); // { ch, th } | null

    const cellAt = (ch, th) => (matrix[ch] && matrix[ch][th]) || '';

    return (
        <div className="max-w-6xl mx-auto pb-8">
            <LessonHeader eyebrow={groupTag} title={(content && content.title) || titleFallback}
                blocks={blocks} done={done} />
            <p className="font-serif italic text-book-muted text-base -mt-4 mb-6">Every cell is a ready-to-quote essay point. Click to expand.</p>

            {(chars.length === 0 || themes.length === 0) ? (
                <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>
            ) : (
                <>
                    <div className="matrix-scroll-container custom-scrollbar">
                        <table className="matrix-table">
                            <thead>
                                <tr>
                                    <th className="matrix-corner"></th>
                                    {chars.map((c, ci) => (<th key={`${c}-${ci}`} className="matrix-col-head">{c}</th>))}
                                </tr>
                            </thead>
                            <tbody>
                                {themes.map((theme, ti) => (
                                    <tr key={`${theme}-${ti}`}>
                                        <th scope="row" className="matrix-row-head">{theme}</th>
                                        {chars.map((ch, ci) => {
                                            const isActive = active && active.ch === ch && active.th === theme;
                                            return (
                                                <td key={`${ch}-${ci}`} onClick={() => setActive(isActive ? null : { ch, th: theme })}
                                                    className={`matrix-cell ${isActive ? 'matrix-cell-active' : ''}`}>
                                                    <div className="line-clamp-3">{cellAt(ch, theme)}</div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {active && (
                        <div className="mt-5 border border-book-accent bg-book-accent/5 p-5">
                            <div className="font-display text-book-accent text-xs mb-2 font-semibold">{active.th} / {active.ch}</div>
                            <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap">{cellAt(active.ch, active.th) || <span className="italic text-book-muted">Empty cell.</span>}</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
